import { store } from './store.js'
import router from './../router/index.js'
import Swal from 'sweetalert2';

const expiredCookie = 'expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

function getCookieValue(name) {
    const prefix = `${name}=`;
    const cookie = document.cookie
        .split(';')
        .map((item) => item.trim())
        .find((item) => item.startsWith(prefix));

    return cookie ? cookie.substring(prefix.length) : '';
}

function getCookieJson(name) {
    const value = getCookieValue(name);

    if (!value) {
        return [];
    }

    try {
        return [JSON.parse(decodeURIComponent(value))];
    } catch (error) {
        console.warn(`No se pudo leer la cookie ${name}.`, error);
        return [];
    }
}

function sanitizeAuthData(data) {
    if (!data || typeof data !== 'object') {
        return data;
    }

    const sanitizedData = { ...data };

    if (sanitizedData.user && typeof sanitizedData.user === 'object') {
        const userWithoutPassword = { ...sanitizedData.user };
        delete userWithoutPassword.password;
        sanitizedData.user = userWithoutPassword;
    }

    return sanitizedData;
}

let Security = {
    sanitizeAuthData: function(data){
        return sanitizeAuthData(data);
    },

    setCookieJson: function(name, value, expires){
        document.cookie = `${name}=${encodeURIComponent(JSON.stringify(value))}; ${expires}; path=/`;
    },

    saveSessionData: function(data, expires){
        const sessionData = this.sanitizeAuthData(data);

        this.setCookieJson("_site_data", sessionData, expires);
        this.setCookieJson("roles", sessionData.user?.roles || [], expires);
        this.setCookieJson("sucursal", sessionData.user?.sucursal || [], expires);

        return sessionData;
    },

    hydrateSessionFromCookie: function(){
        const sessionData = this.getUser()[0];

        if (!sessionData?.token?.token) {
            return false;
        }

        const sanitizedData = this.sanitizeAuthData(sessionData);

        store.token = sanitizedData.token.token;
        store.user = sanitizedData.user || {};
        store.roles = sanitizedData.user?.roles || [];
        store.sucursal = sanitizedData.user?.sucursal || [];

        if (sessionData.user?.password) {
            document.cookie = `_site_data=${encodeURIComponent(JSON.stringify(sanitizedData))}; path=/`;
        }

        return true;
    },

    hasLocalSession: function(){
        return store.token !== "" || this.hydrateSessionFromCookie();
    },

    requireToken: function(){
        if (!this.hasLocalSession()) {
            router.push("/");
            return false
        }

        return true
    },

    requestOptions: function(payload){
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append("Authorization", "Bearer " + store.token);

        return {
            method: "POST",
            body: JSON.stringify(payload),
            headers: headers,
        }
    },

    requestOptionsGet: function(){
        this.hydrateSessionFromCookie();
        const headers = new Headers();
        headers.append("Authorization", "Bearer " + store.token);

        return {
            method: "GET",
            headers: headers,
        }
    },

    requestOptionsJson: function(method, payload){
        this.hydrateSessionFromCookie();
        const headers = new Headers();
        headers.append("Content-Type", "application/json");
        headers.append("Authorization", "Bearer " + store.token);

        return {
            method: method,
            body: JSON.stringify(payload),
            headers: headers,
        }
    },

    requestOptionsImagen: function(formData){
        const headers = new Headers();
        headers.append("Authorization", "Bearer " + store.token);

        return {
            method: "POST",
            body: formData,
            headers: headers,
        }
    },

    authenticatedBlobUrl: async function(path){
        this.hydrateSessionFromCookie();

        const headers = new Headers();
        headers.append("Authorization", "Bearer " + store.token);

        const response = await fetch(process.env.VUE_APP_API_URL + path, {
            method: "GET",
            headers: headers,
        });

        if (!response.ok) {
            throw new Error("No se pudo obtener el archivo protegido");
        }

        const blob = await response.blob();
        return URL.createObjectURL(blob);
    },

    clearSessionCookies: function(){
        document.cookie = `_site_data=; ${expiredCookie}`;
        document.cookie = `roles=; ${expiredCookie}`;
        document.cookie = `sucursal=; ${expiredCookie}`;
    },

    clearSessionState: function(){
        store.token = "";
        store.user = {};
        store.roles = [];
        store.sucursal = [];
        this.clearSessionCookies();
    },

    expireSession: function(showMessage = true){
        this.clearSessionState();

        if (!showMessage) {
            return;
        }

        Swal.fire({
            icon: "error",
            title: 'La Sesion ha expirado',
            showConfirmButton: false,
            timer: 1500,
        });

        setTimeout(() => {
            location.reload();
            window.location.href = "/";
        }, 1500);
    },

    getTokenValidationResult: function(data){
        const responseData = data?.data;
        const valid = responseData === true || responseData?.valid === true;
        const reason = String(data?.message || responseData?.reason || "").toLowerCase();

        return { valid, reason };
    },

    shouldExpireSession: function(data){
        const result = this.getTokenValidationResult(data);
        return !result.valid && (result.reason === "expired" || result.reason === "invalid");
    },

    checkToken: function(){
        return this.checkTokenValid({ showExpiredMessage: true });
    },

    checkTokenValid: function(options = {}){
        this.hydrateSessionFromCookie();

        if(store.token === ""){
            return Promise.resolve(false);
        }

        const payload = {
            token: store.token,
        }

        const headers = new Headers();
        headers.append("Content-Type", "application/json");

        const requestOptions = {
            method: "POST",
            body: JSON.stringify(payload),
            headers: headers,
        }

        return fetch(process.env.VUE_APP_API_URL + "/validate-token", requestOptions)
            .then((response) => response.json())
            .then((data)=>{
                if(data.error){
                    console.warn(data.message || data.error);
                    return null;
                }

                const validation = this.getTokenValidationResult(data);

                if (validation.valid) {
                    return true;
                }

                if (this.shouldExpireSession(data)) {
                    this.expireSession(options.showExpiredMessage === true);
                    return false;
                }

                console.warn("Validacion de token inconclusa; se mantiene la sesion activa.", data);
                return null;
            })
            .catch((error) => {
                console.warn("No se pudo validar el token temporalmente; se mantiene la sesion activa.", error);
                return null;
            })
    },

    secureReload: function(){
        window.onbeforeunload = function() {
            return "Data will be lost if you leave the page, are you sure?";
        };
    },

    getUser: function(){
        return getCookieJson('_site_data');
    },

    getUserSucursal: function(){
        return getCookieJson('sucursal');
    },

    getUserRoles: function(){
        const roles = getCookieJson('roles');

        if (roles.length > 0) {
            return roles;
        }

        if (store.roles?.length) {
            return [store.roles];
        }

        const sessionRoles = this.getUser()[0]?.user?.roles;
        return Array.isArray(sessionRoles) ? [sessionRoles] : [];
    }
}

export default Security;
